var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import IsBetween from 'dayjs/plugin/isBetween';
import { Button } from '../button';
import { Text } from '../text';
import { dayjs } from './dayjs';
dayjs.extend(IsBetween);
var displayName = 'CalendarBody';
export var CalendarBody = function (_a) {
    var checkDateSelected = _a.checkDateSelected, checkDateHighlighted = _a.checkDateHighlighted, classes = _a.classes, disabled = _a.disabled, highlightedRanges = _a.highlightedRanges, _b = _a.highlightToday, highlightToday = _b === void 0 ? true : _b, onSelectedChange = _a.onSelectedChange, maxSelectableDate = _a.maxSelectableDate, minSelectableDate = _a.minSelectableDate, _c = _a.range, range = _c === void 0 ? false : _c, rangeAnchor = _a.rangeAnchor, rangeTuple = _a.rangeTuple, selected = _a.selected, setRangeAnchor = _a.setRangeAnchor, setRangeHover = _a.setRangeHover, date = _a.startingDate, _d = _a.tabIndex, tabIndex = _d === void 0 ? 0 : _d, translate = _a.translate;
    var todayRef = React.useRef(dayjs().startOf('day'));
    var dateShouldBeDisabled = React.useCallback(function (date) {
        if (minSelectableDate && maxSelectableDate) {
            return !dayjs(date).isBetween(dayjs(minSelectableDate).subtract(1, 'day'), dayjs(maxSelectableDate).add(1, 'day'));
        }
        else if (minSelectableDate) {
            return !dayjs(date).isAfter(dayjs(minSelectableDate).subtract(1, 'day'));
        }
        else if (maxSelectableDate) {
            return !dayjs(date).isBefore(dayjs(maxSelectableDate).add(1, 'day'));
        }
        return false;
    }, [maxSelectableDate, minSelectableDate]);
    // renderable memos
    var calendarBody = React.useMemo(function () {
        var d = dayjs(date);
        var startOfMonth = d.startOf('month');
        var endOfMonth = d.endOf('month');
        // Set explicitly to the start of the next month just in case there are any time values that are causing this to be non-zero
        var startOfNextMonth = startOfMonth.endOf('month').add(1, 'day').startOf('month');
        var iter = dayjs(d.startOf('week').toISOString());
        var startOfNextWeek = dayjs(d.endOf('week').add(1, 'day').startOf('day').toISOString());
        var dayLabels = [];
        var dayCellProps = [];
        var selectedDays = [];
        var highlightedDays = [];
        // DayJS uses zero-index for representing the day of the week (0 === sunday and 6 === saturday)
        var dayStartPadCells = [];
        dayStartPadCells.length = startOfMonth.get('day');
        for (var i = 0; i < dayStartPadCells.length; i++) {
            dayStartPadCells[i] = _jsx("div", { className: classes.calendarDay }, "pad-start-day-".concat(i));
        }
        var dayEndPadCells = [];
        dayEndPadCells.length = 6 - endOfMonth.get('day');
        for (var i = 0; i < dayEndPadCells.length; i++) {
            dayEndPadCells[i] = _jsx("div", { className: classes.calendarDay }, "pad-end-day-".concat(i));
        }
        while (!iter.isSame(startOfNextWeek, 'date')) {
            dayLabels.push(_jsx(Text, __assign({ className: classes.calendarDay, component: "div" }, { children: translate('datepicker.dayName', { date: iter.toISOString() }) }), iter.toISOString()));
            iter = iter.add(1, 'day');
        }
        iter = dayjs(startOfMonth.toISOString());
        var _loop_1 = function () {
            var _a;
            var dayISO = iter.toISOString();
            // local callbacks
            var handleDayClick = function () {
                if (range) {
                    if (!rangeAnchor)
                        setRangeAnchor(dayISO);
                    else if (rangeTuple.every(Boolean)) {
                        // user wants to STOP with the range tuple as-is
                        // we need to check the case where a user is selecting a range
                        // and if they clicked on the same end date as the start,
                        // we need to clone the values
                        onSelectedChange === null || onSelectedChange === void 0 ? void 0 : onSelectedChange(rangeTuple.length === 1 ? [rangeTuple[0], rangeTuple[0]] : rangeTuple);
                        setRangeAnchor('');
                        setRangeHover('');
                    }
                }
                else
                    onSelectedChange === null || onSelectedChange === void 0 ? void 0 : onSelectedChange(dayISO);
            };
            var handleDayEnter = function () {
                // the anchor NEVER changes
                // we're just going to set this arbitrary value and return the tuple "above" as a memo
                if (rangeAnchor)
                    setRangeHover(dayISO);
            };
            var dayNumber = translate('datepicker.dayNumber', { date: dayISO });
            var selected_1 = checkDateSelected(dayISO);
            var highlighted = checkDateHighlighted(dayISO);
            if (selected_1)
                selectedDays.push(dayISO);
            if (highlighted)
                highlightedDays.push(dayISO);
            var props = {
                className: clsx(classes.calendarDay, (_a = {},
                    _a[classes.calendarDaySelected] = selected_1,
                    _a[classes.calendarDayToday] = highlightToday && todayRef.current.isSame(dayISO),
                    _a[classes.calendarDayHighlighted] = highlighted,
                    _a[classes.calendarDayDisabled] = dateShouldBeDisabled(dayISO),
                    _a)),
                children: _jsx("span", { children: dayNumber }),
                'data-calendardaynumber': dayNumber,
                key: dayISO,
                onClick: handleDayClick,
                onFocus: handleDayEnter,
                onMouseEnter: handleDayEnter,
                disabled: dateShouldBeDisabled(dayISO),
                variant: 'tertiary',
            };
            dayCellProps.push(props);
            iter = iter.add(1, 'day');
        };
        while (!iter.isSame(startOfNextMonth, 'month')) {
            _loop_1();
        }
        /**
         * Now, we need to check to make sure that we always have 6 rows, regardless
         * of whether the month needs 6 rows to display. This is to fix the bug reported
         * via https://jira.netflix.net/browse/HAWK-1249 where the calendar height
         * can jump around.
         *
         * So, if we always need 6 rows and 7 days per row, we'll need to ensure
         * we have 42 cells and pad the end accordingly
         */
        var numToPad = 6 * 7 - dayStartPadCells.length - dayCellProps.length - dayEndPadCells.length;
        for (var i = 0; i < numToPad; i++) {
            dayEndPadCells.push(_jsx("div", { className: classes.calendarDay }, "pad-end-day-".concat(6 * 7 - i)));
        }
        return (_jsxs(_Fragment, { children: [dayLabels, dayStartPadCells, dayCellProps.map(function (props) {
                    var _a;
                    var _b, _c;
                    // key is the day
                    var dayISO = props.key;
                    var selectedIndex = selectedDays.indexOf(dayISO);
                    var isSelected = selectedIndex > -1;
                    var isHighlighted = highlightedDays.includes(dayISO);
                    var selectedIsRange = !rangeAnchor && Array.isArray(selected) && selected.length === 2;
                    var firstInRange = isSelected;
                    var lastInRange = isSelected;
                    if ((isSelected && selectedDays.length > 1) || (isHighlighted && highlightedDays.length > 1)) {
                        if (selectedIndex === 0 || isHighlighted) {
                            firstInRange =
                                (selectedIsRange && dayjs(dayISO).isSame(selected[0])) ||
                                    (rangeTuple.length > 0 && dayjs(dayISO).isSame(rangeTuple[0])) ||
                                    ((_b = highlightedRanges === null || highlightedRanges === void 0 ? void 0 : highlightedRanges.some(function (_a) {
                                        var _b = __read(_a, 1), hStart = _b[0];
                                        return dayjs(dayISO).isSame(hStart);
                                    })) !== null && _b !== void 0 ? _b : false);
                        }
                        else
                            firstInRange = false;
                        if (selectedIndex === selectedDays.length - 1 || isHighlighted) {
                            lastInRange =
                                (selectedIsRange && dayjs(dayISO).isSame(selected[1])) ||
                                    (rangeTuple.length > 1 && dayjs(dayISO).isSame(rangeTuple[1])) ||
                                    ((_c = highlightedRanges === null || highlightedRanges === void 0 ? void 0 : highlightedRanges.some(function (_a) {
                                        var _b = __read(_a, 2), hEnd = _b[1];
                                        return dayjs(dayISO).isSame(hEnd);
                                    })) !== null && _c !== void 0 ? _c : false);
                        }
                        else
                            lastInRange = false;
                    }
                    return (_jsx(Button, __assign({}, props, { className: clsx(props.className, (_a = {},
                            _a[classes.calendarDayRangeFirstInstance] = firstInRange,
                            _a[classes.calendarDayRangeLastInstance] = lastInRange,
                            _a)), disabled: disabled || props.disabled, tabIndex: tabIndex })));
                }), dayEndPadCells] }));
    }, [
        classes.calendarDay,
        classes.calendarDaySelected,
        classes.calendarDayToday,
        classes.calendarDayHighlighted,
        classes.calendarDayDisabled,
        classes.calendarDayRangeFirstInstance,
        classes.calendarDayRangeLastInstance,
        checkDateSelected,
        checkDateHighlighted,
        date,
        disabled,
        highlightToday,
        highlightedRanges,
        dateShouldBeDisabled,
        onSelectedChange,
        range,
        rangeAnchor,
        rangeTuple,
        setRangeAnchor,
        setRangeHover,
        selected,
        tabIndex,
        translate,
    ]);
    return (_jsx("div", __assign({ className: classes.calendarBody, tabIndex: tabIndex }, { children: calendarBody })));
};
CalendarBody.displayName = displayName;
